import { alpha, createTheme, type Theme } from '@mui/material/styles';
import { brand, office2007 } from './palette';

export type ColorMode = 'light' | 'dark';

/**
 * The selectable visual themes. Each supports both colour modes, so the light/dark toggle keeps
 * working whichever theme is chosen.
 */
export type ThemeName = 'fluent' | 'outlook2007';

export interface ThemeDescriptor {
  id: ThemeName;
  label: string;
  description: string;
}

/** Shown in the theme picker, in this order. The first is the default. */
export const themeCatalogue: readonly ThemeDescriptor[] = [
  { id: 'fluent', label: 'Outlook (Fluent)', description: 'Flat Microsoft 365 look — Outlook blue over a neutral grey canvas.' },
  { id: 'outlook2007', label: 'Outlook 2007', description: 'Office 2007 Blue scheme in light mode, Black scheme in dark — gradients and the orange highlight.' },
];

export const defaultThemeName: ThemeName = 'fluent';

/**
 * Theme-specific "chrome" tokens for the parts of the shell that a palette alone cannot express —
 * the app bar surface, the navigation selection and the search box. Layout components read these
 * instead of hard-coding colours, so adding a theme never means touching the layout.
 */
export interface ChromePalette {
  /** CSS background for the app bar (a colour or a gradient). */
  appBarBackground: string;
  appBarText: string;
  appBarBorder: string;
  searchBackground: string;
  searchHoverBackground: string;
  navSelectedBackground: string;
  navSelectedHoverBackground: string;
  navSelectedText: string;
  navSelectedAccent: string;
  /** Colour of navigation links that are not selected, and their icons. */
  navText: string;
  /** CSS background of the navigation pane. */
  navBackground: string;
  /** Face and border of a navigation link that is not selected — Outlook 2007 draws each as a button. */
  navItemBackground: string;
  navItemBorder: string;
  /** Border of the selected link (Fluent keeps only its left accent bar). */
  navSelectedBorder: string;
  /** Vertical gap between links, in theme spacing units. */
  navItemSpacing: number;
  tableHeadBackground: string;
  tableHeadText: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    chrome: ChromePalette;
  }
  interface PaletteOptions {
    chrome?: ChromePalette;
  }
}

// Segoe UI is the native Microsoft Outlook / Office typeface (present on Windows); the stack falls
// back cleanly on other platforms. Headings and body share it for a consistent Fluent feel.
const fluentFontStack = [
  '"Segoe UI"',
  '"Segoe UI Web (West European)"',
  '-apple-system',
  'BlinkMacSystemFont',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

// Office 2007 shipped with Segoe UI on Vista and fell back to Tahoma on XP.
const office2007FontStack = ['"Segoe UI"', 'Tahoma', 'Verdana', 'Arial', 'sans-serif'].join(',');

/**
 * On a phone a dialog takes the whole screen.
 *
 * Done centrally as a paper style rather than by passing `fullScreen` to each of the two dozen
 * dialogs in the app: the prop would have to be threaded through every call site and kept in step,
 * and one missed dialog is one that traps its content in a ~330px box. Several of them carry tables
 * or multi-field rows that simply do not fit, so this is a functional fix rather than a cosmetic
 * one. Desktop is untouched. Shared by every theme.
 */
const fullScreenDialogOnPhones = {
  styleOverrides: {
    paper: ({ theme }: { theme: Theme }) => ({
      [theme.breakpoints.down('sm')]: {
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        maxHeight: '100%',
        borderRadius: 0,
      },
    }),
  },
};

/** Builds the theme for the requested look and colour mode. */
/** The organisation's own colours, when it has set them (ToR 5.0(iii)); the Fluent look takes them, the Outlook 2007 look keeps its period chrome. */
export interface BrandOverrides { primary?: string | null; secondary?: string | null }

export function createAppTheme(mode: ColorMode, themeName: ThemeName = defaultThemeName, overrides?: BrandOverrides): Theme {
  return themeName === 'outlook2007' ? createOutlook2007Theme(mode) : createFluentTheme(mode, overrides);
}

/**
 * The Microsoft Outlook / Fluent look: the recognisable Outlook-blue app bar and accents over a
 * neutral Office-grey canvas, Segoe UI type, and flat, lightly-rounded (4px) Fluent surfaces.
 */
function createFluentTheme(mode: ColorMode, overrides?: BrandOverrides): Theme {
  const primary = overrides?.primary ?? brand.primary;
  const secondary = overrides?.secondary ?? brand.secondary;
  const isLight = mode === 'light';
  const appBarBg = isLight ? primary : '#0A2A44';
  // Interactive accent for on-surface controls (selected tabs, etc.). The primary blue is a little
  // dark on the near-black dark background, so dark mode uses a lighter blue.
  const interactiveAccent = isLight ? primary : (overrides?.primary ?? '#4CA0E0');
  const divider = isLight ? '#EDEBE9' : alpha('#FFFFFF', 0.09);

  return createTheme({
    palette: {
      mode,
      primary: {
        // The primary blue is a little dark on the near-black dark background, so dark mode uses a
        // lighter blue with dark contrast text (contained buttons then read as dark text on light
        // blue). The branded app bar is styled separately and stays Outlook blue in both modes.
        main: interactiveAccent,
        light: brand.primaryLight,
        dark: brand.primaryDark,
        contrastText: isLight ? '#FFFFFF' : '#0A2A44',
      },
      secondary: {
        main: secondary,
        light: brand.secondaryLight,
        dark: brand.secondaryDark,
        contrastText: '#FFFFFF',
      },
      success: { main: brand.accent, contrastText: '#FFFFFF' },
      background: {
        default: isLight ? brand.background : '#1B1A19',
        paper: isLight ? brand.paper : '#252423',
      },
      text: {
        primary: isLight ? '#201F1E' : '#F3F2F1',
        secondary: isLight ? '#605E5C' : '#C8C6C4',
      },
      divider,
      chrome: {
        appBarBackground: appBarBg,
        appBarText: '#FFFFFF',
        appBarBorder: isLight ? brand.primaryDark : alpha('#FFFFFF', 0.08),
        searchBackground: alpha('#FFFFFF', isLight ? 0.16 : 0.1),
        searchHoverBackground: alpha('#FFFFFF', isLight ? 0.24 : 0.16),
        // Outlook folder-list selection: a blue left accent bar over a light blue fill.
        navSelectedBackground: alpha(interactiveAccent, isLight ? 0.1 : 0.2),
        navSelectedHoverBackground: alpha(interactiveAccent, isLight ? 0.14 : 0.26),
        navSelectedText: isLight ? '#201F1E' : '#F3F2F1',
        navSelectedAccent: interactiveAccent,
        navText: isLight ? '#201F1E' : '#F3F2F1',
        navBackground: isLight ? brand.paper : '#252423',
        navItemBackground: 'transparent',
        navItemBorder: 'transparent',
        navSelectedBorder: 'transparent',
        navItemSpacing: 0.25,
        tableHeadBackground: isLight ? '#FAF9F8' : '#2B2A29',
        tableHeadText: isLight ? '#201F1E' : '#F3F2F1',
      },
    },
    shape: { borderRadius: 4 },
    typography: {
      fontFamily: fluentFontStack,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: { styleOverrides: { body: { scrollbarWidth: 'thin' } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 4,
            border: `1px solid ${divider}`,
            // Fluent depth-4 style shadow — subtle and neutral.
            boxShadow: isLight
              ? '0 0.3px 0.9px rgba(0, 0, 0, 0.10), 0 1.6px 3.6px rgba(0, 0, 0, 0.13)'
              : '0 1px 2px rgba(0, 0, 0, 0.5)',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: appBarBg,
            backgroundImage: 'none',
            color: '#FFFFFF',
            borderBottom: `1px solid ${isLight ? brand.primaryDark : alpha('#FFFFFF', 0.08)}`,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 4 } },
      },
      MuiDialog: fullScreenDialogOnPhones,
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: interactiveAccent } } },
      MuiTab: { styleOverrides: { root: { '&.Mui-selected': { color: interactiveAccent } } } },
    },
  });
}

/**
 * The Outlook 2007 look. Light mode is the Office 2007 Blue scheme: a pale-blue window frame with
 * white panes, sky-blue gradient chrome carrying navy text, gradient buttons, gradient group
 * headers, and the orange selection highlight. Dark mode is the Black scheme: charcoal gradients,
 * silver text, the same orange. Corners are barely rounded (3px), as they were.
 */
function createOutlook2007Theme(mode: ColorMode): Theme {
  const isLight = mode === 'light';
  const o = isLight ? office2007.blue : office2007.black;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: o.navy,
        light: o.navyLight,
        dark: o.navyDark,
        contrastText: isLight ? '#FFFFFF' : '#1B1B1B',
      },
      secondary: {
        main: '#FFB700',
        light: '#FFD58D',
        dark: '#E39B00',
        contrastText: '#1B1B1B',
      },
      success: { main: isLight ? '#3C8C2F' : '#7BC96F', contrastText: isLight ? '#FFFFFF' : '#1B1B1B' },
      background: { default: o.frame, paper: o.pane },
      text: { primary: o.text, secondary: o.textSecondary },
      divider: o.borderSoft,
      chrome: {
        appBarBackground: o.chromeGradient,
        appBarText: isLight ? o.navy : o.text,
        appBarBorder: o.border,
        searchBackground: isLight ? alpha('#FFFFFF', 0.85) : alpha('#FFFFFF', 0.08),
        searchHoverBackground: isLight ? '#FFFFFF' : alpha('#FFFFFF', 0.14),
        navSelectedBackground: o.selectionGradient,
        navSelectedHoverBackground: o.selectionHoverGradient,
        navSelectedText: o.selectionText,
        navSelectedAccent: o.selectionBorder,
        // Outlook 2007 draws its navigation links in navy on a pale-blue pane.
        navText: isLight ? o.navy : o.navyLight,
        navBackground: isLight
          ? 'linear-gradient(180deg, #F3F8FE 0%, #DFEAF8 100%)'
          : 'linear-gradient(180deg, #454545 0%, #353535 100%)',
        // Each link is a button, as the Outlook 2007 navigation pane drew its modules: a pale-blue
        // gradient face with a blue border, and the orange face only for the current one.
        navItemBackground: isLight
          ? 'linear-gradient(180deg, #E9F2FC 0%, #CFE0F5 50%, #BDD4F0 100%)'
          : 'linear-gradient(180deg, #5E5E5E 0%, #4A4A4A 50%, #404040 100%)',
        navItemBorder: o.border,
        navSelectedBorder: o.selectionBorder,
        navItemSpacing: 0.75,
        tableHeadBackground: o.headerGradient,
        tableHeadText: isLight ? o.navy : o.text,
      },
    },
    shape: { borderRadius: 3 },
    typography: {
      fontFamily: office2007FontStack,
      h1: { fontWeight: 700, color: o.navy },
      h2: { fontWeight: 700, color: o.navy },
      h3: { fontWeight: 700, color: o.navy },
      h4: { fontWeight: 700, color: o.navy },
      h5: { fontWeight: 600, color: o.navy },
      h6: { fontWeight: 600, color: o.navy },
      subtitle1: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: { styleOverrides: { body: { scrollbarWidth: 'thin' } } },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: o.border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 3,
            border: `1px solid ${o.border}`,
            boxShadow: isLight ? '0 1px 2px rgba(21, 66, 139, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            backgroundImage: o.headerGradient,
            borderBottom: `1px solid ${o.borderSoft}`,
            paddingTop: 10,
            paddingBottom: 10,
          },
          title: { color: isLight ? o.navy : o.text, fontWeight: 600 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: o.chromeBase,
            backgroundImage: o.chromeGradient,
            color: isLight ? o.navy : o.text,
            borderBottom: `1px solid ${o.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: o.pane,
            backgroundImage: isLight
              ? 'linear-gradient(180deg, #F3F8FE 0%, #DFEAF8 100%)'
              : 'linear-gradient(180deg, #454545 0%, #353535 100%)',
            borderRight: `1px solid `,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 3 },
          containedPrimary: {
            backgroundImage: o.buttonGradient,
            border: `1px solid ${o.navyDark}`,
            '&:hover': { backgroundImage: 'none', backgroundColor: o.navyDark },
          },
          outlined: { borderColor: o.border },
        },
      },
      MuiDialog: fullScreenDialogOnPhones,
      MuiDialogTitle: {
        styleOverrides: {
          root: { backgroundImage: o.headerGradient, color: isLight ? o.navy : o.text, borderBottom: `1px solid ${o.borderSoft}` },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundImage: o.headerGradient,
              color: isLight ? o.navy : o.text,
              fontWeight: 600,
              borderBottom: `1px solid ${o.border}`,
            },
          },
        },
      },
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: o.selectionBorder, height: 3 } } },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            '&.Mui-selected': { color: isLight ? o.navy : o.text },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 3 } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? '#FFFFE1' : '#4A4A4A',
            color: isLight ? '#1E1E1E' : '#F0F0F0',
            border: `1px solid ${isLight ? '#767676' : '#6E6E6E'}`,
            fontSize: 12,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundImage: o.selectionHoverGradient, color: o.selectionText },
            '&:hover .MuiListItemIcon-root': { color: o.selectionText },
          },
        },
      },
    },
  });
}
